import { Copy, Download, ShieldCheck, Smartphone } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState, type FormEvent } from "react";
import { Alert, Button, Field, Input } from "../../components/ui";
import { authClient } from "../../lib/auth-client";

/**
 * TOTP enrolment: confirm password → scan QR → verify a code → save backup
 * codes. Used both for mandatory enrolment (Owner/Admin) and voluntarily.
 */
export function MfaSetup({ onComplete }: { onComplete: () => void }) {
  const [stage, setStage] = useState<"password" | "scan" | "codes">("password");
  const [password, setPassword] = useState("");
  const [uri, setUri] = useState("");
  const [qr, setQr] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (uri)
      QRCode.toString(uri, { type: "svg", margin: 1, errorCorrectionLevel: "M" }).then((svg) => setQr(`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`));
  }, [uri]);

  const secret = uri ? (new URL(uri).searchParams.get("secret") ?? "") : "";

  async function start(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: err } = await authClient.twoFactor.enable({ password });
    setBusy(false);
    setPassword("");
    if (err || !data || data.method !== "totp")
      return setError(err?.status === 401 || err?.status === 400 ? "That password is incorrect." : (err?.message ?? "Could not start setup."));
    setUri(data.totpURI);
    setBackupCodes(data.backupCodes);
    setStage("scan");
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, "") });
    setBusy(false);
    if (err) {
      setCode("");
      return setError("That code didn't match. Make sure your device's clock is correct and try the newest code.");
    }
    // Don't refresh the session yet: that would navigate away before the
    // backup codes are shown. The caller refreshes in onComplete.
    setStage("codes");
  }

  const downloadCodes = () => {
    const blob = new Blob([`Galeway backup codes\nEach code can be used once.\n\n${backupCodes.join("\n")}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "galeway-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
    setSaved(true);
  };

  if (stage === "password") {
    return (
      <form onSubmit={start} className="space-y-5">
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="Confirm your password" hint="For your security, confirm it's you before setting up two-factor authentication.">
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11"
            />
          )}
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!password} icon={<Smartphone className="size-4" />}>
          Continue
        </Button>
      </form>
    );
  }

  if (stage === "scan") {
    return (
      <form onSubmit={verify} className="space-y-5">
        {error && <Alert tone="danger">{error}</Alert>}
        <ol className="space-y-1 text-[13px] text-muted">
          <li>1. Open an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password…).</li>
          <li>2. Scan this QR code, or enter the setup key manually.</li>
          <li>3. Enter the 6-digit code the app shows.</li>
        </ol>
        <div className="flex flex-col items-center gap-3 rounded-xl border border-line bg-white p-4">
          {qr ? <img src={qr} alt="QR code for your authenticator app" className="size-44" /> : <div className="size-44 animate-pulse rounded bg-slate-100" />}
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2">
          <code className="flex-1 break-all font-mono text-[12.5px] text-muted">{secret.replace(/(.{4})/g, "$1 ").trim()}</code>
          <Button variant="ghost" size="sm" icon={<Copy className="size-3.5" />} onClick={() => navigator.clipboard?.writeText(secret)}>
            Copy
          </Button>
        </div>
        <Field label="6-digit code">
          {(p) => (
            <Input
              {...p}
              required
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="h-12 text-center font-mono text-lg tracking-[0.3em]"
              placeholder="000000"
            />
          )}
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={code.replace(/\s/g, "").length !== 6}>
          Verify and enable
        </Button>
      </form>
    );
  }

  return (
    <div className="space-y-5">
      <Alert tone="success" title="Two-factor authentication is on">
        Save these backup codes somewhere safe. Each one can be used once if you lose access to your authenticator. They won't be shown again.
      </Alert>
      <ul className="grid grid-cols-2 gap-2 rounded-xl border border-line bg-surface-2 p-4 font-mono text-[13px]">
        {backupCodes.map((c) => (
          <li key={c} className="text-center">
            {c}
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1" icon={<Download className="size-4" />} onClick={downloadCodes}>
          Download
        </Button>
        <Button
          variant="secondary"
          className="flex-1"
          icon={<Copy className="size-4" />}
          onClick={() => {
            navigator.clipboard?.writeText(backupCodes.join("\n"));
            setSaved(true);
          }}
        >
          Copy
        </Button>
      </div>
      <Button size="lg" className="w-full" disabled={!saved} onClick={onComplete} icon={<ShieldCheck className="size-4" />}>
        I've saved my codes
      </Button>
    </div>
  );
}
